import { createEntityAdapter, EntityState } from "@reduxjs/toolkit";
import api from "services/api";
import { PortfolioV2Api } from "services/generated";
import { handleErrors } from "services/helpers";
import { rootApi } from "store/root-api";
import { signalRService } from "utils/signalr";
import { Holding } from "./types/Holding";
import { HoldingsUpdatedMessage } from "./types/HoldingsUpdatedMessage";

const portfolioClient = new PortfolioV2Api(
    undefined,
    undefined,
    api,
);

const ratesAdapter = createEntityAdapter<Holding, string>({ selectId: (holding) => holding.productCode })

export const holdingsApi = rootApi.injectEndpoints({
    endpoints: builder => ({
        getHoldings: builder.query<EntityState<Holding, string>, void>({
            queryFn: async () => {
                const response = await handleErrors(() => portfolioClient.getPortfolioHoldings());
                if (response.data) {
                    const holdings = response.data.map(d => ({ productCode: d.productCode!, totalAmount: d.totalAmount!, reservedAmount: d.reservedAmount!, wac: d.wac!, lastChangedAt: d.lastChangedAt! }));
                    return { data: ratesAdapter.addMany(ratesAdapter.getInitialState(), holdings) }
                }
                return { error: response.error }
            },
            onCacheEntryAdded: async (arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved, dispatch }) => {
                try {
                    await cacheDataLoaded;
                    await signalRService.subscribeEvent<HoldingsUpdatedMessage>("HoldingsUpdatedMessage", (data) => {
                        updateCachedData((draft) => {
                            ratesAdapter.upsertMany(draft, data.holdings.map(h => ({ productCode: h.productCode, totalAmount: h.totalAmount, reservedAmount: h.reservedAmount, wac: h.wac, lastChangedAt: h.lastChangedAt })))
                        });

                        // We want to refetch portfolio g/l after holdings are changed since the cashflows might have changed too
                        dispatch(holdingsApi.util.invalidateTags(['PortfolioGainLoss']));
                    }, () => {
                        // In case the connection was lost, we want to refetch the holdings
                        dispatch(holdingsApi.endpoints.getHoldings.initiate(undefined, { subscribe: false, forceRefetch: true }));
                        dispatch(holdingsApi.util.invalidateTags(['PortfolioGainLoss']));
                    });
                } catch (error) {
                    console.error(error)
                }

                await cacheEntryRemoved;
                await signalRService.unsubscribeEvent("HoldingsUpdatedMessage");
            },
            keepUnusedDataFor: 60 * 1000,
        }),
    }),
});

export const { useGetHoldingsQuery } = holdingsApi;

export function useGetProductHolding(productCode?: string) {
    return useGetHoldingsQuery(undefined, {
        selectFromResult: ({ data, isLoading }) => ({
            data: data == null ? null :
                Object.entries(data.entities).find(([key, value]) => key.toUpperCase() == productCode?.toUpperCase())?.[1], isLoading
        }),
        skip: !productCode
    });
}